import React, { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useAppActions, useAppState } from '../../../overmind';
import { NavLink, useNavigate } from 'react-router';
import { KeplerState } from '../../../models/kepler-state';
import { SSOButton } from '../sso-button/sso-button';
import { ResetPassword } from '../../../models/view/reset-password';
import { AccountPageLoaderLayer } from '../../../components/general/loading-state/loader-layers/account-page-loader-layer/account-page-loader-layer';
import { FetchType } from '../../../enums';
import { PagePath } from '../../../navigation/navigation.enums';
import { Button, Checkbox, ConfirmationPasswordValidator, EmailValidator, FieldController, RequiredValidator, TextField, useMatchScreenWidth } from '@keplerco/core';
import { PasswordStrength } from '../password-strength/password-strength';
import { themedAssetUrl } from '../../../library/helpers/themed-asset-url';
import accountStyles from '../account.module.css';
import classNames from 'classnames';
import { TertiaryButton } from '../../../components/general/tertiary-button/tertiary-button';

export function SetPasswordPage(): JSX.Element {
  const params = new URLSearchParams(window.location.search);
  const navigate = useNavigate();

  const actions = useAppActions();
  const { passwordErrors, samlUrl } = useAppState<KeplerState>();

  const { control, handleSubmit, getValues, watch, setError } = useForm({ mode: 'onBlur', reValidateMode: 'onBlur' });

  const isMobile = useMatchScreenWidth('mobile');

  const [fieldValues, setFieldValues] = useState<FieldValues>();

  useEffect(() => {
    watch(setFieldValues);
  }, []);

  return (
    <div className={accountStyles.accountBackground} style={{ backgroundImage: `url(${themedAssetUrl('backgrounds/pattern.background.svg')})` }}>
      <div className={accountStyles.accountPage}>
        <div className={classNames('card', accountStyles.accountCard)}>
          <AccountPageLoaderLayer path={PagePath.accountSetPassword}>
            {!isMobile && (
              <div className={accountStyles.accountNavigationToggler}>
                <span className="body">Already have an account?</span>
                {/* TODO: replace with Anchor once it's refactored to have NavLink functionality as well */}
                <TertiaryButton type="button" onClick={() => navigate(`${PagePath.accountBase}${PagePath.accountLogin}`)}>
                  Sign in
                </TertiaryButton>
              </div>
            )}

            <h1 id="set-password-heading">Welcome to Kepler</h1>
            <div style={{ marginTop: 12 }}>Fill in your company email address and set your password</div>

            <form
              id="setPassword"
              className={accountStyles.accountForm}
              autoComplete="off"
              onSubmit={handleSubmit(async fieldValues => {
                actions.startLoader({ path: PagePath.accountSetPassword, type: FetchType.PageFetching });
                fieldValues.code = params.get('code')?.toString();
                await actions.setPasswordAndLogin(fieldValues as ResetPassword);
                actions.stopLoader(PagePath.accountSetPassword);
              })}
            >
              <FieldController
                control={control}
                name="email"
                rules={new EmailValidator()}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="email"
                    label="Email"
                    responsive
                    validation={{
                      dirty: fieldState.isDirty || !!fieldState.error,
                      invalid: !!fieldState.error,
                      message: fieldState.error?.message ?? 'Enter your email',
                    }}
                  />
                )}
              />

              <FieldController
                control={control}
                name="password"
                rules={new RequiredValidator('Enter your password')}
                render={({ field, fieldState }) => (
                  <div style={{ width: '100%', marginBottom: 12 }}>
                    <TextField
                      {...field}
                      type="password"
                      label="Password"
                      responsive
                      validation={{
                        dirty: fieldState.isDirty || !!fieldState.error,
                        invalid: !!fieldState.error,
                        message: '',
                      }}
                    />

                    <PasswordStrength
                      password={fieldValues?.password}
                      onPercentage={percent => {
                        if (percent < 100) return;
                        actions.clearPasswordErrors();
                      }}
                    />
                  </div>
                )}
              />

              <FieldController
                control={control}
                name="confirmPassword"
                rules={new ConfirmationPasswordValidator('Enter your confirmation password', getValues('password'))}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="password"
                    label="Confirm Password"
                    responsive
                    validation={{
                      dirty: fieldState.isDirty || !!fieldState.error,
                      invalid: !!fieldState.error,
                      message: fieldState.error?.message ?? 'Enter your confirmation password',
                    }}
                  />
                )}
              />

              <FieldController
                control={control}
                name="terms"
                rules={new RequiredValidator(true)}
                render={({ field, fieldState }) => (
                  <React.Fragment>
                    <div style={{ textAlign: 'start', marginBottom: 15 }}>
                      <Checkbox {...field} id="termsAndConditions" checked={getValues('terms')} clickablearea="checkbox">
                        <label htmlFor="terms" className="body">
                          By signing up, you agree to our <NavLink to={'https://legal.keplerco.io/documents/terms-of-use/'}>terms of service</NavLink> and <NavLink to={'https://legal.keplerco.io/documents/privacy-policy/'}>privacy policy</NavLink>
                        </label>
                      </Checkbox>
                    </div>

                    {!!fieldState.error && <div className="fieldErrorMessage">Please agree to our terms of service and privacy policy before continuing</div>}
                  </React.Fragment>
                )}
              />

              <Button type="button">Activate your account</Button>

              {passwordErrors !== undefined &&
                passwordErrors?.length > 0 &&
                passwordErrors.map((error: string, index: number) => {
                  return (
                    <React.Fragment key={'error_' + index}>
                      <div className="formErrorMessage">{error}</div>
                    </React.Fragment>
                  );
                })}
            </form>

            <div className={accountStyles.accountDivider}>Or activate using</div>

            {!!samlUrl && (
              <SSOButton
                ssoType="saml"
                onClick={async () => {
                  if (!!getValues('terms')) {
                    window.location.href = `${samlUrl}?returnUrl=${params.get('kt')},,,${params.get('code')}`;
                  } else {
                    setError('terms', { type: 'focus' }, { shouldFocus: true });
                  }
                }}
              />
            )}

            <SSOButton
              ssoType="google"
              onClick={() => {
                if (!!getValues('terms')) {
                  actions.externalLogin({ provider: 'Google', kt: params.get('kt'), code: params.get('code') });
                } else {
                  setError('terms', { type: 'focus' }, { shouldFocus: true });
                }
              }}
            />

            <SSOButton
              ssoType="microsoft"
              onClick={() => {
                if (!!getValues('terms')) {
                  actions.externalLogin({ provider: 'Microsoft', kt: params.get('kt'), code: params.get('code') });
                } else {
                  setError('terms', { type: 'focus' }, { shouldFocus: true });
                }
              }}
            />

            <SSOButton
              ssoType="linkedin"
              onClick={() => {
                if (!!getValues('terms')) {
                  actions.externalLogin({ provider: 'LinkedIn', kt: params.get('kt'), code: params.get('code') });
                } else {
                  setError('terms', { type: 'focus' }, { shouldFocus: true });
                }
              }}
            />
          </AccountPageLoaderLayer>
        </div>
      </div>
    </div>
  );
}
